import { useEffect, useState } from 'react';
import axios from '../../../axios';
import Breadcrumb from '../../Breadcrumb/Breadcrumb';
import usePermisos from '../../../hooks/usePermisos';
import NoPermissions from '../../NoPermissions/NoPermissions';
import Loader from '../../../Layout/Loaders/Loader';
import notify from '../../../Utils/notify';
import HolidayModal from './HolidayModal';
import Table, { useTableSort, useTablePagination } from '../../UI/Table';
import {
    PlusIcon,
    TrashIcon,
    PencilIcon,
    CalendarDaysIcon,
    ArrowPathIcon,
    SparklesIcon,
    CheckCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';

const PERMISOS_REQUERIDOS = [
    'holidays_list_read',
    'holidays_create',
    'holidays_update',
    'holidays_delete'
];

const Holidays = () => {
    const { loading: loadingPermisos, tienePermiso } = usePermisos(PERMISOS_REQUERIDOS);
    const [holidays, setHolidays] = useState([]);
    const [holidaysRaw, setHolidaysRaw] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sucursalFiltro, setSucursalFiltro] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [holidayEditar, setHolidayEditar] = useState(null);

    // Hooks para tabla reutilizable
    const { sortedData, sortConfig, handleSort } = useTableSort(holidays, { field: 'fecha', direction: 'DESC' });
    const { 
        paginatedData, 
        page, 
        setPage, 
        itemsPerPage, 
        setItemsPerPage, 
        totalItems 
    } = useTablePagination(sortedData, { page: 1, itemsPerPage: 10 });

    useEffect(() => {
        getSucursales();
        getHolidays();
    }, []);

    const getSucursales = () => {
        axios.get('api/sucursals')
            .then(res => setSucursales(res.data))
            .catch(e => {
                console.log(e, 'Error al obtener sucursales');
                notify.error('Error al cargar sucursales');
            });
    };

    const getHolidays = (sucursalId = '') => {
        setLoading(true);
        const params = sucursalId ? `?sucursal_id=${sucursalId}` : '';
        
        axios.get(`api/holidays${params}`)
            .then(res => {
                setHolidays(res.data);
                setHolidaysRaw(res.data);
                setLoading(false);
            })
            .catch(e => {
                console.log(e, 'Error al obtener festivos');
                setLoading(false);
                notify.error('Error al cargar días festivos');
            });
    };

    const handleFiltroSucursal = (sucursalId) => {
        setSucursalFiltro(sucursalId);
        setPage(1); // Reiniciar a página 1 al filtrar
        if (sucursalId === '') {
            setHolidays(holidaysRaw);
        } else if (sucursalId === 'global') {
            setHolidays(holidaysRaw.filter(h => h.sucursal_id === null));
        } else {
            setHolidays(holidaysRaw.filter(h => h.sucursal_id === null || h.sucursal_id === parseInt(sucursalId)));
        }
    };

    const handleDelete = (holiday) => {
        if (window.confirm(`¿Realmente deseas eliminar el festivo "${holiday.nombre}"?`)) {
            axios.delete(`api/holidays/${holiday.id}`)
                .then(res => {
                    setHolidays(holidays.filter(h => h.id !== holiday.id));
                    setHolidaysRaw(holidaysRaw.filter(h => h.id !== holiday.id));
                    notify.success(res.data.message || 'Festivo eliminado correctamente');
                })
                .catch(e => {
                    console.log("Error:", e);
                    notify.error('Error al eliminar el festivo');
                });
        }
    };

    const handleEdit = (holiday) => {
        setHolidayEditar(holiday);
        setModalOpen(true);
    };

    const handleCreate = () => {
        setHolidayEditar(null);
        setModalOpen(true);
    };

    const handleModalClose = (refresh = false) => {
        setModalOpen(false);
        setHolidayEditar(null);
        if (refresh) {
            getHolidays(sucursalFiltro);
        }
    };

    const importarFestivosNacionales = () => {
        const year = new Date().getFullYear();
        if (window.confirm(`¿Deseas importar los festivos nacionales de México para el año ${year}?`)) {
            const loadingToast = notify.loading('Importando festivos...');
            axios.post('api/holidays/importar-nacionales', {
                year: year,
                sucursal_id: null // null = aplica a todas
            })
                .then(res => {
                    notify.dismiss(loadingToast);
                    notify.success(res.data.message || 'Festivos importados correctamente');
                    getHolidays(sucursalFiltro);
                })
                .catch(e => {
                    notify.dismiss(loadingToast);
                    console.log("Error:", e);
                    notify.error('Error al importar festivos');
                });
        }
    };

    const getSucursalNombre = (sucursalId) => {
        if (!sucursalId) return 'Todas las sucursales';
        const suc = sucursales.find(s => s.id === sucursalId);
        return suc ? suc.nombre_sucursal : 'Desconocida';
    };

    const formatFecha = (fecha) => {
        const date = new Date(fecha + 'T00:00:00');
        return date.toLocaleDateString('es-MX', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const formatFechaCorta = (fecha) => {
        const date = new Date(fecha + 'T00:00:00');
        return date.toLocaleDateString('es-MX', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        });
    };

    const getDiaSemana = (fecha) => {
        const date = new Date(fecha + 'T00:00:00');
        return date.toLocaleDateString('es-MX', { weekday: 'long' });
    };

    return (
        <div>
            <div className="container-admin-menu">
                <Breadcrumb hasButton={false} hasFilters={false} />
            </div>
            <div className="container-admin-container">
                <div className="container-admin bg-white rounded-lg shadow-md p-4">

                    {/* Modal de crear/editar */}
                    <HolidayModal
                        open={modalOpen}
                        onClose={handleModalClose}
                        holiday={holidayEditar}
                        sucursales={sucursales}
                    />

                    {loadingPermisos ? (
                        <Loader />
                    ) : tienePermiso('holidays_list_read') ? (
                        <>
                            {/* Header con filtros y acciones */}
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 info-badge rounded-lg p-4 mb-4">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                    {/* Filtro de sucursal */}
                                    <div className="w-full sm:w-64">
                                        <label className="block text-xs font-base text-white mb-1">Filtrar por Sucursal</label>
                                        <select
                                            value={sucursalFiltro}
                                            onChange={(e) => handleFiltroSucursal(e.target.value)}
                                            className="w-full browser-default dento-input"
                                        >
                                            <option value="">Todas</option>
                                            <option value="global">Solo Globales</option>
                                            {sucursales.map((sucursal) => (
                                                <option value={sucursal.id} key={sucursal.id}>{sucursal.nombre_sucursal}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex gap-2">
                                    {/* Botón importar festivos nacionales */}
                                    {tienePermiso('holidays_create') && (
                                        <button
                                            onClick={importarFestivosNacionales}
                                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm cursor-pointer"
                                        >
                                            <SparklesIcon className="h-5 w-5" />
                                            Importar Festivos México
                                        </button>
                                    )}

                                    {/* Botón crear festivo */}
                                    {tienePermiso('holidays_create') && (
                                        <button
                                            onClick={handleCreate}
                                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#11cdef] text-white rounded-lg text-sm font-medium hover:bg-[#0ea5e9] transition-colors shadow-sm cursor-pointer"
                                        >
                                            <PlusIcon className="h-5 w-5" />
                                            Nuevo Festivo
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Tabla de festivos */}
                            <Table
                                columns={[
                                    {
                                        key: 'fecha',
                                        label: 'Fecha',
                                        sortable: true,
                                        render: (value) => (
                                            <div className="flex items-center gap-2">
                                                <CalendarDaysIcon className="h-5 w-5 text-blue-500" />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {formatFechaCorta(value)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {getDiaSemana(value)}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'nombre',
                                        label: 'Nombre',
                                        sortable: true,
                                        render: (value) => (
                                            <div className="text-sm font-medium text-gray-900">{value}</div>
                                        )
                                    },
                                    {
                                        key: 'descripcion',
                                        label: 'Descripción',
                                        render: (value) => (
                                            <div className="text-sm text-gray-600">{value || '-'}</div>
                                        )
                                    },
                                    {
                                        key: 'sucursal_id',
                                        label: 'Sucursal',
                                        sortable: true,
                                        render: (value) => (
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                value === null 
                                                    ? 'bg-purple-100 text-purple-800' 
                                                    : 'bg-blue-100 text-blue-800'
                                            }`}>
                                                {getSucursalNombre(value)}
                                            </span>
                                        )
                                    },
                                    {
                                        key: 'recurrente',
                                        label: 'Recurrente',
                                        align: 'center',
                                        sortable: true,
                                        render: (value) => value ? (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                <ArrowPathIcon className="h-3.5 w-3.5" />
                                                Anual
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 text-xs">-</span>
                                        )
                                    },
                                    {
                                        key: 'activo',
                                        label: 'Estado',
                                        align: 'center',
                                        sortable: true,
                                        render: (value) => value ? (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <CheckCircleIcon className="h-3.5 w-3.5" />
                                                Activo
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                <XCircleIcon className="h-3.5 w-3.5" />
                                                Inactivo
                                            </span>
                                        )
                                    }
                                ]}
                                data={paginatedData}
                                actions={[
                                    {
                                        type: 'edit',
                                        tooltip: 'Editar',
                                        icon: PencilIcon,
                                        permission: 'holidays_update',
                                        onClick: (row) => handleEdit(row)
                                    },
                                    {
                                        type: 'delete',
                                        tooltip: 'Eliminar',
                                        icon: TrashIcon,
                                        permission: 'holidays_delete',
                                        onClick: (row) => handleDelete(row)
                                    }
                                ]}
                                permissions={{
                                    holidays_update: tienePermiso('holidays_update'),
                                    holidays_delete: tienePermiso('holidays_delete')
                                }}
                                sorting={{
                                    field: sortConfig.field,
                                    direction: sortConfig.direction,
                                    onSort: handleSort
                                }}
                                pagination={{
                                    page: page,
                                    itemsPerPage: itemsPerPage,
                                    totalItems: totalItems,
                                    onPageChange: setPage,
                                    onItemsPerPageChange: setItemsPerPage,
                                    itemsPerPageOptions: [10, 25, 50, 100]
                                }}
                                loading={loading}
                                emptyMessage="No hay días festivos registrados"
                                emptyComponent={
                                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                                        <CalendarDaysIcon className="h-12 w-12 text-gray-400" />
                                        <p className="text-gray-500 font-medium">No hay días festivos registrados</p>
                                        {tienePermiso('holidays_create') && (
                                            <button
                                                onClick={handleCreate}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-[#11cdef] text-white rounded-lg text-sm font-medium hover:bg-[#0ea5e9] transition-colors cursor-pointer"
                                            >
                                                <PlusIcon className="h-5 w-5" />
                                                Agregar primer festivo
                                            </button>
                                        )}
                                    </div>
                                }
                                striped={true}
                                hoverable={true}
                                responsive={true}
                            />

                            {/* Información adicional */}
                            {holidays.length > 0 && (
                                <div className="mt-4 bg-blue-50 rounded-lg p-4">
                                    <div className="flex items-start gap-2">
                                        <CalendarDaysIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                                        <div className="text-sm text-blue-800">
                                            <p className="font-medium mb-1">Información sobre días festivos:</p>
                                            <ul className="list-disc list-inside space-y-1 text-xs">
                                                <li>Los festivos <strong>globales</strong> aplican a todas las sucursales</li>
                                                <li>Los festivos <strong>recurrentes</strong> se repiten cada año automáticamente</li>
                                                <li>Al agregar días a la agenda, los festivos activos se <strong>omiten automáticamente</strong></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <NoPermissions />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Holidays;
